"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import { Menu, X, LogOut, Heart, ShoppingCart, Search, Store, Info, Mail, User } from "lucide-react"
import { useWishlist } from "@/lib/wishlist-context"
import { useCart } from "@/lib/cart-context"
import { Badge } from "@/components/ui/badge"
import { usePathname } from "next/navigation"

interface NavbarProps {
  isAdmin?: boolean
  onLogout?: () => void
}

export function Navbar({ isAdmin = false, onLogout }: NavbarProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const { wishlistCount } = useWishlist()
  const { getTotalItems } = useCart()
  const cartCount = getTotalItems()
  const pathname = usePathname()

  // Détecter le scroll pour ajouter un effet
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Fermer le menu mobile lors du changement de route
  useEffect(() => {
    setIsOpen(false)
  }, [pathname])

  const isActive = (path: string) => pathname === path

  return (
    <nav
      className={`bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 sticky top-0 z-50 transition-all duration-300 ${scrolled ? "shadow-lg bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm" : "shadow-sm"
        }`}
    >
      <div className="container flex justify-between items-center h-16 px-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <Link
          href={isAdmin ? "/admin" : "/"}
          className="flex items-center gap-2 sm:gap-3 font-bold text-lg sm:text-xl text-primary group"
        >
          <div className="w-12 h-10 sm:w-14 sm:h-12  rounded-lg flex items-center justify-center text-white shadow-md group-hover:shadow-lg group-hover:scale-105 transition-all duration-300">
            <img
              src="/logo_sw.png"
              alt="Logo"
              className="w-16 h-14 sm:w-[70px] sm:h-[60px] object-contain"
            />
          </div>
          <span className="hidden sm:block bg-gradient-to-r from-primary to-primary-dark bg-clip-text text-transparent group-hover:from-primary-dark group-hover:to-primary-light transition-all duration-300">
            {process.env.NEXT_PUBLIC_COMPANY_NAME || "StockWise"}
          </span>
        </Link>

        {/* Desktop Menu */}
        <div className="hidden lg:flex items-center gap-1 xl:gap-2">
          {!isAdmin ? (
            <>
              <Link
                href="/"
                className={`px-3 xl:px-4 py-2 rounded-lg text-sm xl:text-base font-medium transition-all duration-300 flex items-center gap-2 ${isActive("/")
                  ? "bg-primary text-white shadow-md"
                  : "text-gray-700 dark:text-gray-300 hover:bg-primary/10 hover:text-primary hover:scale-105"
                  }`}
              >
                <span>Accueil</span>
              </Link>
              <Link
                href="/boutique"
                className={`px-3 xl:px-4 py-2 rounded-lg text-sm xl:text-base font-medium transition-all duration-300 flex items-center gap-2 ${isActive("/boutique")
                  ? "bg-primary text-white shadow-md"
                  : "text-gray-700 dark:text-gray-300 hover:bg-primary/10 hover:text-primary hover:scale-105"
                  }`}
              >
                <Store size={18} className="hidden xl:block" />
                <span>Boutique</span>
              </Link>
              <Link
                href="/recherche"
                className={`px-3 xl:px-4 py-2 rounded-lg text-sm xl:text-base font-medium transition-all duration-300 flex items-center gap-2 ${isActive("/recherche")
                  ? "bg-primary text-white shadow-md"
                  : "text-gray-700 dark:text-gray-300 hover:bg-primary/10 hover:text-primary hover:scale-105"
                  }`}
              >
                <Search size={18} />
                <span className="hidden xl:block">Recherche</span>
              </Link>
              <Link
                href="/a-propos"
                className={`px-3 xl:px-4 py-2 rounded-lg text-sm xl:text-base font-medium transition-all duration-300 flex items-center gap-2 ${isActive("/a-propos")
                  ? "bg-primary text-white shadow-md"
                  : "text-gray-700 dark:text-gray-300 hover:bg-primary/10 hover:text-primary hover:scale-105"
                  }`}
              >
                <Info size={18} className="hidden xl:block" />
                <span>À Propos</span>
              </Link>
              <Link
                href="/contact"
                className={`px-3 xl:px-4 py-2 rounded-lg text-sm xl:text-base font-medium transition-all duration-300 flex items-center gap-2 ${isActive("/contact")
                  ? "bg-primary text-white shadow-md"
                  : "text-gray-700 dark:text-gray-300 hover:bg-primary/10 hover:text-primary hover:scale-105"
                  }`}
              >
                <Mail size={18} className="hidden xl:block" />
                <span>Contact</span>
              </Link>

              {/* Divider */}
              <div className="w-px h-8 bg-gray-300 dark:bg-gray-700 mx-2" />

              <Link
                href="/souhaits"
                className={`px-3 xl:px-4 py-2 rounded-lg text-sm xl:text-base font-medium transition-all duration-300 flex items-center gap-2 relative group ${isActive("/souhaits")
                  ? "bg-red-500 text-white shadow-md"
                  : "text-gray-700 dark:text-gray-300 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-500 hover:scale-105"
                  }`}
              >
                <Heart
                  size={20}
                  className={`transition-all duration-300 ${isActive("/souhaits") ? "fill-white" : "group-hover:fill-red-500"
                    }`}
                />
                <span className="hidden xl:block">Panier</span>
                {wishlistCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center px-1 text-xs bg-red-500 hover:bg-red-600 border-2 border-white dark:border-gray-900 animate-pulse">
                    {wishlistCount}
                  </Badge>
                )}
              </Link>
              <Link
                href="/panier"
                className={`px-3 xl:px-4 py-2 rounded-lg text-sm xl:text-base font-medium transition-all duration-300 flex items-center gap-2 relative group ${isActive("/panier")
                  ? "bg-green-500 text-white shadow-md"
                  : "text-gray-700 dark:text-gray-300 hover:bg-green-50 dark:hover:bg-green-900/20 hover:text-green-500 hover:scale-105"
                  }`}
              >
                <ShoppingCart
                  size={20}
                  className="transition-transform duration-300 group-hover:scale-110"
                />
                <span className="hidden xl:block">Panier</span>
                {cartCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center px-1 text-xs bg-green-500 hover:bg-green-600 border-2 border-white dark:border-gray-900 animate-bounce">
                    {cartCount}
                  </Badge>
                )}
              </Link>
            </>
          ) : (
            <>
              <Link
                href="/admin"
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${isActive("/admin")
                  ? "bg-primary text-white shadow-md"
                  : "text-gray-700 dark:text-gray-300 hover:bg-primary/10 hover:text-primary hover:scale-105"
                  }`}
              >
                Tableau de Bord
              </Link>
              <Link
                href="/admin/produits"
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${isActive("/admin/produits")
                  ? "bg-primary text-white shadow-md"
                  : "text-gray-700 dark:text-gray-300 hover:bg-primary/10 hover:text-primary hover:scale-105"
                  }`}
              >
                Produits
              </Link>
              <Link
                href="/admin/commandes"
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${isActive("/admin/commandes")
                  ? "bg-primary text-white shadow-md"
                  : "text-gray-700 dark:text-gray-300 hover:bg-primary/10 hover:text-primary hover:scale-105"
                  }`}
              >
                Commandes
              </Link>
              <Link
                href="/admin/inventaire"
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${isActive("/admin/inventaire")
                  ? "bg-primary text-white shadow-md"
                  : "text-gray-700 dark:text-gray-300 hover:bg-primary/10 hover:text-primary hover:scale-105"
                  }`}
              >
                Inventaire
              </Link>
              <button
                onClick={onLogout}
                className="px-4 py-2 rounded-lg font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:scale-105 transition-all duration-300 flex items-center gap-2"
              >
                <LogOut size={18} /> Déconnexion
              </button>
            </>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-300 group"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
        >
          {isOpen ? (
            <X size={24} className="text-gray-700 dark:text-gray-300 group-hover:rotate-90 transition-transform duration-300" />
          ) : (
            <Menu size={24} className="text-gray-700 dark:text-gray-300 group-hover:scale-110 transition-transform duration-300" />
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
          }`}
      >
        <div className="container px-4 py-4 space-y-1">
          {!isAdmin ? (
            <>
              <Link
                href="/"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 ${isActive("/")
                  ? "bg-primary text-white shadow-md"
                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:pl-6"
                  }`}
              >
                <span className="font-medium">Accueil</span>
              </Link>
              <Link
                href="/boutique"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 ${isActive("/boutique")
                  ? "bg-primary text-white shadow-md"
                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:pl-6"
                  }`}
              >
                <Store size={20} />
                <span className="font-medium">Boutique</span>
              </Link>
              <Link
                href="/recherche"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 ${isActive("/recherche")
                  ? "bg-primary text-white shadow-md"
                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:pl-6"
                  }`}
              >
                <Search size={20} />
                <span className="font-medium">Recherche</span>
              </Link>
              <Link
                href="/a-propos"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 ${isActive("/a-propos")
                  ? "bg-primary text-white shadow-md"
                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:pl-6"
                  }`}
              >
                <Info size={20} />
                <span className="font-medium">À Propos</span>
              </Link>
              <Link
                href="/contact"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 ${isActive("/contact")
                  ? "bg-primary text-white shadow-md"
                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:pl-6"
                  }`}
              >
                <Mail size={20} />
                <span className="font-medium">Contact</span>
              </Link>

              {/* Divider */}
              <div className="h-px bg-gray-200 dark:bg-gray-800 my-2" />

              <Link
                href="/souhaits"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 relative ${isActive("/souhaits")
                  ? "bg-red-500 text-white shadow-md"
                  : "text-gray-700 dark:text-gray-300 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-500 hover:pl-6"
                  }`}
              >
                <Heart size={20} className={isActive("/souhaits") ? "fill-white" : ""} />
                <span className="font-medium">Mon Panier</span>
                {wishlistCount > 0 && (
                  <Badge className="ml-auto h-6 min-w-6 flex items-center justify-center px-2 text-xs bg-red-500 text-white">
                    {wishlistCount}
                  </Badge>
                )}
              </Link>
              <Link
                href="/panier"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 relative ${isActive("/panier")
                  ? "bg-green-500 text-white shadow-md"
                  : "text-gray-700 dark:text-gray-300 hover:bg-green-50 dark:hover:bg-green-900/20 hover:text-green-500 hover:pl-6"
                  }`}
              >
                <ShoppingCart size={20} />
                <span className="font-medium">Panier</span>
                {cartCount > 0 && (
                  <Badge className="ml-auto h-6 min-w-6 flex items-center justify-center px-2 text-xs bg-green-500 text-white">
                    {cartCount}
                  </Badge>
                )}
              </Link>
            </>
          ) : (
            <>
              <Link
                href="/admin"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 ${isActive("/admin")
                  ? "bg-primary text-white shadow-md"
                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:pl-6"
                  }`}
              >
                <span className="font-medium">Tableau de Bord</span>
              </Link>
              <Link
                href="/admin/produits"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 ${isActive("/admin/produits")
                  ? "bg-primary text-white shadow-md"
                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:pl-6"
                  }`}
              >
                <span className="font-medium">Produits</span>
              </Link>
              <Link
                href="/admin/commandes"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 ${isActive("/admin/commandes")
                  ? "bg-primary text-white shadow-md"
                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:pl-6"
                  }`}
              >
                <span className="font-medium">Commandes</span>
              </Link>
              <Link
                href="/admin/inventaire"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 ${isActive("/admin/inventaire")
                  ? "bg-primary text-white shadow-md"
                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:pl-6"
                  }`}
              >
                <span className="font-medium">Inventaire</span>
              </Link>
              <button
                onClick={onLogout}
                className="flex items-center gap-3 px-4 py-3 rounded-lg w-full text-left font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:pl-6 transition-all duration-300"
              >
                <LogOut size={20} />
                <span>Déconnexion</span>
              </button>
            </>
          )}
        </div>
      </div>
    </nav>
  )
}
